import { useCallback } from "react";
import { FormState, SelectOption, Step } from "../types";
import {
  ALL_DIGITAL_VALUE,
  DIGITAL_TYPE,
  validateField,
  validateStep,
} from "../utils/ValidationUtil";
import { getCompactResponseDetails, getErrorMessage, logError } from "../../../frontendLogger";

interface UseActivityFormHandlersProps {
  step: Step;
  data: FormState;
  setData: React.Dispatch<React.SetStateAction<FormState>>;
  setStep: React.Dispatch<React.SetStateAction<Step>>;
  setErrors: React.Dispatch<React.SetStateAction<Set<string>>>;
  setSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
  errorAlertRef: React.RefObject<HTMLDivElement>;
  postUrl: string;
  initialActivity: Partial<FormState>;
}

export function useActivityFormHandlers({
  step,
  data,
  setData,
  setStep,
  setErrors,
  setSubmitted,
  setIsSubmitting,
  errorAlertRef,
  postUrl,
  initialActivity,
}: UseActivityFormHandlersProps) {
  const onChange = useCallback(
    (k: keyof FormState) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        setData((s) => ({ ...s, [k]: e.target.value })),
    [],
  );

  const onMultiChange = useCallback(
    (k: "municipalities" | "ages" | "types") => (values: SelectOption[]) => {
      if (k !== "municipalities") {
        setData((s) => ({ ...s, [k]: values.map((v) => v.value) }));
        return;
      }
      const hasAll = values.some((v) => v.value === ALL_DIGITAL_VALUE);
      setData((s) =>
        hasAll
          ? {
              ...s,
              municipalities: [ALL_DIGITAL_VALUE],
              types: s.types.includes(DIGITAL_TYPE)
                ? s.types
                : [...s.types, DIGITAL_TYPE],
            }
          : {
              ...s,
              municipalities: values
                .filter((v) => v.value !== ALL_DIGITAL_VALUE)
                .map((v) => v.value),
            },
      );
    },
    [],
  );

  const clearError = useCallback((field: string) => {
    setErrors((prev) => {
      if (!prev.has(field)) return prev;
      const next = new Set(prev);
      next.delete(field);
      return next;
    });
  }, []);

  const onBlur = useCallback(
    (field: keyof FormState) =>
      (_e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        // Always validate the entire step to ensure all required fields are checked
        const stepErrors = validateField(field, data);
        setErrors(stepErrors);
      },
    [data, step],
  );

  const next = useCallback(() => {
    const stepErrors = validateStep(step, data);

    if (stepErrors.size > 0) {
      setErrors(stepErrors);

      setTimeout(() => {
        errorAlertRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
        errorAlertRef.current?.focus();
      }, 150);

      return;
    }

    setStep((s) => (s + 1) as Step);
  }, [step, data, setStep, setErrors, errorAlertRef]);

  const prev = useCallback(() => {
    setStep((s) => (s - 1) as Step);
  }, [setStep]);

  const submit = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const res = await fetch(postUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!res.ok) {
        logError({
          category: "activity-form",
          eventName: "submit-activity-form-failed",
          componentOrWidget: "ActivityForm",
          url: postUrl,
          data: {
            status: res.status,
            step,
            ...(await getCompactResponseDetails(res)),
          },
        });

        return;
      }

      if (res.ok) {
        setSubmitted(true);
      }
    } catch (error) {
      logError({
        category: "activity-form",
        eventName: "submit-activity-form-failed",
        componentOrWidget: "ActivityForm",
        url: postUrl,
        data: {
          status: undefined,
          step,
          error: getErrorMessage(error),
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [data, postUrl, setSubmitted, setIsSubmitting, step]);

  const createNewActivity = useCallback(() => {
    setData((s) => ({ ...s, ...initialActivity }));
    setStep(1);
    setSubmitted(false);
  }, [setData, setStep, setSubmitted, initialActivity]);

  return {
    onChange,
    onMultiChange,
    clearError,
    onBlur,
    next,
    prev,
    submit,
    createNewActivity,
  };
}
